// NOTE: This component is a generic set of inputs for report breakdowns. If additional logic is
// required for a specific report there should be a specialized component created.
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ClickableTag,
  Dropdown,
  MultipleDropdown,
  removeObjectByValue,
  toggleObject,
} from '@makeably/creativex-design-system';
import ChartMetricSelection from 'components/reporting/ChartMetricSelection';
import ReportingDrawer from 'components/reporting/ReportingDrawer';
import {
  isSameByValue,
} from 'utilities/array';
import { track } from 'utilities/mixpanel';
import styles from './BreakdownDrawer.module.css';

const optionProps = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
});
const optionPropsArray = PropTypes.arrayOf(optionProps);

const propTypes = {
  colCounts: optionPropsArray.isRequired,
  isOpen: PropTypes.bool.isRequired,
  metrics: optionPropsArray.isRequired,
  onClose: PropTypes.func.isRequired,
  segments: optionPropsArray.isRequired,
  selectedChartMetrics: optionPropsArray.isRequired,
  selectedColCount: optionProps.isRequired,
  selectedMetrics: optionPropsArray.isRequired,
  selectedSegments: optionPropsArray.isRequired,
  selectedVizType: optionProps.isRequired,
  setSelectedChartMetrics: PropTypes.func.isRequired,
  setSelectedColCount: PropTypes.func.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  setSelectedSegments: PropTypes.func.isRequired,
  setSelectedVizType: PropTypes.func.isRequired,
  vizTypes: optionPropsArray.isRequired,
};

const maxSegments = 3;

function generateSegmentOptions(segments, selected) {
  if (selected.length < maxSegments) {
    return segments;
  }

  return segments.map((dim) => {
    if (selected.findIndex((sel) => sel.value === dim.value) === -1) {
      return {
        ...dim,
        disabled: true,
      };
    }
    return dim;
  });
}

function BreakdownDrawer({
  colCounts,
  isOpen,
  metrics,
  onClose,
  segments,
  selectedChartMetrics,
  selectedColCount,
  selectedSegments,
  selectedMetrics,
  selectedVizType,
  setSelectedChartMetrics,
  setSelectedColCount,
  setSelectedSegments,
  setSelectedMetrics,
  setSelectedVizType,
  vizTypes,
}) {
  const [segmentOptions, setSegmentOptions] = useState(
    generateSegmentOptions(segments, selectedSegments),
  );
  const [currentColCount, setCurrentColCount] = useState(selectedColCount);
  const [currentChartMetrics, setCurrentChartMetrics] = useState(selectedChartMetrics);
  const [currentSegments, setCurrentSegments] = useState(selectedSegments);
  const [currentMetrics, setCurrentMetrics] = useState(selectedMetrics);
  const [currentVizType, setCurrentVizType] = useState(selectedVizType);

  const isApplyDisabled = (isSameByValue(currentSegments, selectedSegments)
    && isSameByValue(currentMetrics, selectedMetrics)
    && currentVizType?.value === selectedVizType?.value
    && currentColCount?.value === selectedColCount?.value
    && isSameByValue(currentChartMetrics || [], selectedChartMetrics || [])
  );

  useEffect(() => {
    setCurrentColCount(selectedColCount);
  }, [selectedColCount]);

  useEffect(() => {
    setCurrentChartMetrics(selectedChartMetrics);
  }, [selectedChartMetrics]);

  useEffect(() => {
    setSegmentOptions(generateSegmentOptions(segments, selectedSegments));
    setCurrentSegments(selectedSegments);
  }, [selectedSegments]);

  useEffect(() => {
    setCurrentMetrics(selectedMetrics);
  }, [selectedMetrics]);

  useEffect(() => {
    setCurrentVizType(selectedVizType);
  }, [selectedVizType]);

  useEffect(() => {
    if (!currentChartMetrics) return;

    setCurrentChartMetrics((prev) => prev.filter((metric) => currentMetrics.includes(metric)));
  }, [currentMetrics]);

  const removeSegment = (option) => {
    const updated = removeObjectByValue(currentSegments, option);
    setSegmentOptions(generateSegmentOptions(segments, updated));
    setCurrentSegments(updated);
  };

  const removeMetric = (option) => {
    setCurrentMetrics((current) => removeObjectByValue(current, option));
  };

  const handleSegmentChange = (selected) => {
    const current = toggleObject(currentSegments, selected);
    setSegmentOptions(generateSegmentOptions(segments, current));
    setCurrentSegments(current);
  };

  const handleSegmentClear = () => {
    const current = [];
    setSegmentOptions(generateSegmentOptions(segments, current, maxSegments));
    setCurrentSegments(current);
  };

  const handleMetricChange = (selected) => {
    setCurrentMetrics(toggleObject(currentMetrics, selected));
  };

  const handleClose = () => {
    setSegmentOptions(generateSegmentOptions(segments, selectedSegments));
    setCurrentChartMetrics(selectedChartMetrics);
    setCurrentColCount(selectedColCount);
    setCurrentSegments(selectedSegments);
    setCurrentMetrics(selectedMetrics);
    setCurrentVizType(selectedVizType);
    onClose();
  };

  const nonChartMetrics = useMemo(() => (
    currentMetrics.filter((metric) => !currentChartMetrics?.includes(metric))
  ), [currentMetrics, currentChartMetrics]);

  const handleApply = () => {
    setSegmentOptions(generateSegmentOptions(segments, currentSegments, maxSegments));
    setSelectedSegments(currentSegments);
    setSelectedMetrics(currentChartMetrics.concat(nonChartMetrics));
    setSelectedChartMetrics?.(currentChartMetrics);
    setSelectedColCount?.(currentColCount);
    setSelectedVizType?.(currentVizType);

    track('apply_setup_changes', {
      chartMetrics: currentChartMetrics,
      colCount: currentColCount?.value,
      metrics: currentMetrics,
      segments: currentSegments,
      vizType: currentVizType?.value,
    });
    onClose();
  };

  return (
    <ReportingDrawer
      header="Select Report Details"
      isApplyDisabled={isApplyDisabled}
      isOpen={isOpen}
      subheader="Choose what metrics you want to see and how you want to see them visualized."
      onApply={handleApply}
      onClose={handleClose}
    >
      <Dropdown
        label="Visualization Type"
        menuProps={{ size: 'large' }}
        options={vizTypes}
        selected={currentVizType}
        size="large"
        onChange={(type) => setCurrentVizType(type)}
      />
      <Dropdown
        label="Number of Columns Showing"
        menuProps={{ size: 'large' }}
        options={colCounts}
        selected={currentColCount}
        size="large"
        onChange={(num) => setCurrentColCount(num)}
      />
      <div className="u-flexRow u-alignEnd u-gap-16">
        <MultipleDropdown
          label={`Group by (Select up to ${maxSegments})`}
          menuProps={{ size: 'large' }}
          options={segmentOptions}
          selected={currentSegments}
          size="large"
          onChange={handleSegmentChange}
        />
        { currentSegments.length > 0 && (
          <Button
            label="Clear"
            variant="tertiary"
            onClick={handleSegmentClear}
          />
        ) }
      </div>
      { currentSegments.length > 0 && (
        <div className={styles.selections}>
          { currentSegments.map((segment) => (
            <ClickableTag
              key={segment.value}
              label={segment.label}
              onRemove={segment.disabled ? undefined : (() => removeSegment(segment))}
            />
          )) }
        </div>
      ) }
      <div className="u-flexRow u-alignEnd u-gap-16">
        <MultipleDropdown
          label="Metrics"
          menuProps={{ size: 'large' }}
          options={metrics}
          selected={currentMetrics}
          size="large"
          onChange={handleMetricChange}
        />
        { currentMetrics.length > 0 && (
          <Button
            label="Clear"
            variant="tertiary"
            onClick={() => setCurrentMetrics([])}
          />
        ) }
      </div>
      <ChartMetricSelection
        chartMetrics={currentChartMetrics}
        label="Select up to 2 Metrics to Show in Chart"
        nonChartMetrics={nonChartMetrics}
        setChartMetrics={setCurrentChartMetrics}
        onRemoveMetric={removeMetric}
      />
    </ReportingDrawer>
  );
}

BreakdownDrawer.propTypes = propTypes;

export default BreakdownDrawer;
